import type { AdminEmailMessageBodyProps } from "../../models/admin-email.models";
import { ADMIN_EMAIL_ACTION_CLASS } from "../../utils/admin-email.constants";
import { formatAdminEmailDate } from "../../utils/admin-email.utils";

// Display received content as text so email HTML cannot execute or load remote tracking resources.
// Keep the provider's truncation notice visible rather than presenting a shortened body as complete.
const AdminEmailMessageBody = ({
  response,
  loading,
  error,
  loadingMore,
  continuationError,
  loadMore,
}: AdminEmailMessageBodyProps) => {
  const message = response?.message;

  return (
    <article data-test="admin-email-message-body" aria-busy={loading} className="min-w-0 p-5">
      {loading && (
        <p role="status" className="text-sm opacity-70">
          Loading message…
        </p>
      )}
      {error && (
        <p role="alert" data-test="admin-email-body-error" className="text-sm text-[#BF0002]">
          {error}
        </p>
      )}
      {response && message && (
        <>
          <h2 className="break-words text-xl font-semibold">{message.subject || "(No subject)"}</h2>
          <dl className="mt-4 space-y-2 border-b border-gray-200 pb-4 text-xs dark:border-gray-700">
            <div>
              <dt className="inline font-semibold">From: </dt>
              <dd className="inline break-all">
                {message.from.name
                  ? `${message.from.name} <${message.from.address}>`
                  : message.from.address || "Unknown sender"}
              </dd>
            </div>
            <div>
              <dt className="inline font-semibold">Inbox: </dt>
              <dd className="inline break-all">{response.mailbox.address}</dd>
            </div>
            <div>
              <dt className="inline font-semibold">Received: </dt>
              <dd className="inline">
                <time dateTime={message.receivedDateTime}>{formatAdminEmailDate(message.receivedDateTime)}</time>
              </dd>
            </div>
          </dl>
          {message.bodyTruncated && (
            <p data-test="admin-email-body-truncated" className="mt-4 rounded bg-[#FFF8E1] p-3 text-xs text-[#946200]">
              This message is long. More text is available below.
            </p>
          )}
          <pre className="mt-5 max-h-[520px] overflow-auto whitespace-pre-wrap break-words font-sans text-sm leading-6">
            {message.body || "This message has no text content."}
          </pre>
          {message.nextBodyOffset !== null && message.nextBodyOffset !== undefined && (
            <button
              type="button"
              data-test="admin-email-load-more-body"
              className={`${ADMIN_EMAIL_ACTION_CLASS} mt-4`}
              disabled={loadingMore}
              onClick={() => void loadMore()}
            >
              {loadingMore ? "Loading message text…" : "Load more message"}
            </button>
          )}
          {continuationError && (
            <p role="alert" data-test="admin-email-body-continuation-error" className="mt-3 text-xs text-[#BF0002]">
              {continuationError}
            </p>
          )}
          {message.hasAttachments && (
            <p className="mt-4 text-xs opacity-60">
              This email has attachments. Attachment downloads are unavailable here.
            </p>
          )}
        </>
      )}
    </article>
  );
};

export default AdminEmailMessageBody;
